// THE ANALYZE AND VALUE STEPS' SHARED CHROME (WEB-PARITY-4): the app's words stamped with their id, its BrandCard with an
// icon and a tint, the value number line as inline SVG, the case-strength bars, the progress lines and a sheet. Chrome
// only: nothing here decides anything — every figure arrives from the server and every word from `parity4Wording.ts`.

import { Fragment, useEffect, useId, useRef, useState, type ReactNode, type RefObject } from "react";
import type { EvaluateCaseResponse } from "@contract";
import { money } from "../../format";
import { lineGeometry, type Tint } from "./parity4Rules";
import { say4, say4With, type Parity4WordingId } from "./parity4Wording";

type NumberLineFacts = NonNullable<EvaluateCaseResponse["body"]["numberLine"]>;

/** One of the app's sentences, stamped with its id (`data-held-copy`), as every word this client holds is. */
export function Said4({
  id,
  as: Tag = "span",
  className,
  values,
}: {
  id: Parity4WordingId;
  as?: "h1" | "h2" | "h3" | "h4" | "p" | "span" | "b" | "small" | "strong";
  className?: string;
  values?: Readonly<Record<string, string | number>>;
}) {
  return (
    <Tag className={className} data-held-copy={id}>
      <Marked text={values === undefined ? say4(id) : say4With(id, values)} />
    </Tag>
  );
}

/** The app's inline Markdown — `**bold**` and `*italic*` — which SwiftUI renders in a `LocalizedStringKey` (ValueView.swift
 *  :410, :1336, :1508). Any other character is text. */
export function Marked({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*\s][^*]*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**") && part.length > 4) return <strong key={i}>{part.slice(2, -2)}</strong>;
        if (part.startsWith("*") && part.endsWith("*") && part.length > 2) return <em key={i}>{part.slice(1, -1)}</em>;
        return <Fragment key={i}>{part}</Fragment>;
      })}
    </>
  );
}

// ── icons: the app's SF Symbols, drawn plainly ─────────────────────────────────────────────────────────────────────

export type IconName =
  | "sparkles" | "target" | "seal" | "scale" | "diamond" | "question" | "shield" | "hand" | "star" | "warn" | "bars"
  | "wrench" | "wand" | "check" | "back" | "info" | "pin" | "arrowDown" | "circle" | "checkCircle" | "chevron" | "range"
  | "pencil" | "doc" | "house" | "trend"
  // WEB-PARITY-5: the Letter step's and the overlay's other symbols (magnifyingglass, photo.on.rectangle,
  // list.bullet.clipboard, bolt.fill, plus).
  | "search" | "photo" | "list" | "bolt" | "plus" | "tick";

const PATHS: Record<IconName, string> = {
  sparkles: "M9 2l1.6 4.4L15 8l-4.4 1.6L9 14l-1.6-4.4L3 8l4.4-1.6zM18 12l.9 2.1L21 15l-2.1.9L18 18l-.9-2.1L15 15l2.1-.9zM17 2l.6 1.4L19 4l-1.4.6L17 6l-.6-1.4L15 4l1.4-.6z",
  target: "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 3a7 7 0 1 1 0 14 7 7 0 0 1 0-14zm0 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm0 2.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3z",
  seal: "M12 1.5l2.4 1.9 3-.4 1.1 2.8 2.8 1.1-.4 3 1.9 2.4-1.9 2.4.4 3-2.8 1.1-1.1 2.8-3-.4L12 22.5l-2.4-1.9-3 .4-1.1-2.8-2.8-1.1.4-3L1.2 12l1.9-2.4-.4-3 2.8-1.1L6.6 3l3 .4zm4.3 7.1l-1.4-1.4-4.4 4.5-2-2-1.4 1.4 3.4 3.4z",
  scale: "M12 3v2M4 8h16M6 8l-3 6a3 3 0 0 0 6 0zM18 8l-3 6a3 3 0 0 0 6 0zM12 5v15M8 20h8",
  diamond: "M12 2l10 10-10 10L2 12zm0 5a2.5 2.5 0 0 0-2.5 2.5h1.6a.9.9 0 1 1 1.4.7c-.8.5-1.3 1-1.3 2v.3h1.6v-.2c0-.4.2-.6.8-1A2.5 2.5 0 0 0 12 7zm-.8 7.5v1.6h1.6v-1.6z",
  question: "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 4a3.3 3.3 0 0 1 3.4 3.2c0 1.5-1 2.2-1.8 2.8-.6.4-.8.7-.8 1.3V14h-1.8v-.8c0-1.3.6-1.9 1.4-2.5.6-.4 1.2-.8 1.2-1.5 0-.8-.7-1.4-1.6-1.4-.9 0-1.6.6-1.7 1.5H8.5A3.4 3.4 0 0 1 12 6zm-.9 9.4h1.8v1.8h-1.8z",
  shield: "M12 2l8 3v6c0 5-3.4 9.4-8 11-4.6-1.6-8-6-8-11V5zm3.6 7.2L11 13.8l-2.6-2.6-1.4 1.4 4 4 6-6z",
  hand: "M9 3a1.5 1.5 0 0 1 3 0v7h1V4a1.5 1.5 0 0 1 3 0v7h1V6.5a1.5 1.5 0 0 1 3 0V15c0 4-3 7-7 7s-6-2-7.5-4.5L3 13.6a1.5 1.5 0 0 1 2.4-1.8L7.5 14V5a1.5 1.5 0 0 1 3 0v-2z",
  star: "M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3 6.1 20.6l1.3-6.6-4.9-4.6 6.6-.8z",
  warn: "M12 2L1 21h22zm-1 7h2v6h-2zm0 8h2v2h-2z",
  bars: "M4 20V10h3v10zm6.5 0V4h3v16zM17 20v-7h3v7z",
  wrench: "M22.7 19 13.6 9.9c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4Z",
  wand: "M3 21l11-11 2 2L5 23zM15 3l1 2.5L18.5 6 16 7l-1 2.5L14 7l-2.5-1L14 5zM20 9l.6 1.4L22 11l-1.4.6L20 13l-.6-1.4L18 11l1.4-.6z",
  check: "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm4.6 7.6-5.6 5.6-3.2-3.2 1.4-1.4 1.8 1.8 4.2-4.2z",
  back: "M9 5 3 11l6 6v-4h6a4 4 0 0 1 0 8h-2v2h2a6 6 0 0 0 0-12H9z",
  info: "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1 5h2v2h-2zm0 4h2v6h-2z",
  pin: "M12 2a7 7 0 0 0-7 7c0 5.2 7 13 7 13s7-7.8 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z",
  arrowDown: "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm3.5 6.5L10 14h4v2H7V9h2v3.6l5.1-5.1z",
  circle: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zm0 2a7 7 0 1 1 0 14 7 7 0 0 1 0-14z",
  checkCircle: "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm4.6 7.6-5.6 5.6-3.2-3.2 1.4-1.4 1.8 1.8 4.2-4.2z",
  chevron: "M9 5l7 7-7 7-1.4-1.4L13.2 12 7.6 6.4z",
  range: "M2 12l4-4v3h12V8l4 4-4 4v-3H6v3z",
  pencil: "M3 17.2V21h3.8L18 9.8 14.2 6zM20.7 7.1a1 1 0 0 0 0-1.4l-2.4-2.4a1 1 0 0 0-1.4 0l-1.8 1.8 3.8 3.8z",
  doc: "M6 2h9l5 5v15H6zm8 1.5V8h4.5zM9 12h7v1.5H9zm0 3h7v1.5H9z",
  house: "M12 3.2 2.8 11a1 1 0 0 0 1.3 1.5l.9-.8V20a1 1 0 0 0 1 1h4.2v-5.4h3.6V21H18a1 1 0 0 0 1-1v-8.3l.9.8a1 1 0 0 0 1.3-1.5Z",
  trend: "M3 6l6 6 4-4 8 8v-5h2v9h-9v-2h5.6L13 11l-4 4-7.4-7.4z",
  search: "M10 3a7 7 0 0 1 5.6 11.2l5.1 5.1-1.4 1.4-5.1-5.1A7 7 0 1 1 10 3zm0 2a5 5 0 1 0 0 10 5 5 0 0 0 0-10z",
  photo: "M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1zm1 2v8.6l4-4 3 3 2-2 5 5V7zm10.5 1a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3z",
  list: "M9 2h6v2h3a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3zm0 7v2h2V9zm4 0v2h4V9zm-4 4v2h2v-2zm4 0v2h4v-2z",
  bolt: "M13 2 4 14h6l-1 8 9-12h-6z",
  plus: "M11 4h2v7h7v2h-7v7h-2v-7H4v-2h7z",
  tick: "M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z",
};

const STROKED: ReadonlySet<IconName> = new Set(["scale"]);

export function Icon({ name, size = 18, className }: { name: IconName; size?: number; className?: string }) {
  const stroked = STROKED.has(name);
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d={PATHS[name]}
        fill={stroked ? "none" : "currentColor"}
        stroke={stroked ? "currentColor" : undefined}
        strokeWidth={stroked ? 1.8 : undefined}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The app's `BrandCard` (Shared/BrandCard.swift): an icon, a title, the body, a muted footer — tinted by its accent. */
export function Card4({
  icon,
  titleId,
  title,
  tint = "primary",
  titleTint = false,
  footer,
  children,
  ...marks
}: {
  icon: IconName;
  titleId?: Parity4WordingId;
  title?: string;
  tint?: Tint;
  titleTint?: boolean;
  footer?: ReactNode;
  children: ReactNode;
} & Record<`data-${string}`, string | number | boolean | undefined>) {
  return (
    <section className={`card4 t4-${tint}`} {...marks}>
      <h3 className={titleTint ? "card4-title tinted" : "card4-title"}>
        <Icon name={icon} size={16} className="card4-icon" />
        {titleId !== undefined ? <Said4 id={titleId} /> : <span>{title}</span>}
      </h3>
      <div className="card4-body">{children}</div>
      {footer !== undefined && <div className="card4-footer">{footer}</div>}
    </section>
  );
}

/** The app's `ProcessingStatusBar`: its lines, one at a time, with a long-run note after a while where it has one. */
export function ProgressLines4({ ids, longRun }: { ids: readonly Parity4WordingId[]; longRun?: Parity4WordingId }) {
  const [i, setI] = useState(0);
  const [long, setLong] = useState(false);
  useEffect(() => {
    const timer = setInterval(() => setI((n) => (n + 1) % ids.length), 1400);
    const late = setTimeout(() => setLong(true), 20_000);
    return () => {
      clearInterval(timer);
      clearTimeout(late);
    };
  }, [ids.length]);
  return (
    <div className="progress4" role="status">
      <p className="search-progress" data-held-copy={ids[i]}>
        <span className="spinner" aria-hidden="true" />
        {say4(ids[i])}
      </p>
      {long && longRun !== undefined && <Said4 id={longRun} as="p" className="progress4-long" />}
    </div>
  );
}

/** The case-strength meter's five capsules (Shared/Motion.swift:40-90), the first `filled` lit. */
export function MeterBars({ filled, total = 5 }: { filled: number; total?: number }) {
  return (
    <div className="meter-bars" aria-hidden="true">
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className={i < filled ? "on" : undefined} />
      ))}
    </div>
  );
}

// ── the value number line (ValueNumberLine.swift), as inline SVG ───────────────────────────────────────────────────

function useWidth<T extends HTMLElement>(fallback: number): [RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    const measure = () => setWidth(Math.max(200, Math.round(el.getBoundingClientRect().width)));
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

/** The app's centerpiece chart: the track, the comps' emerald band, the assessed marker, the "Comps imply" marker at the
 *  band's midpoint, and the amber bracket under the gap, labelled `−$X · −N%`. Labels are nudged to stay inside, 60px from
 *  either edge, as the app's are (:105-118). Drawn only where the server sent the gap. */
export function NumberLine({ line }: { line: NumberLineFacts & { gap: NonNullable<NumberLineFacts["gap"]> } }) {
  const [ref, w] = useWidth<HTMLDivElement>(320);
  const assessed = line.assessed?.amount ?? 0;
  const g = lineGeometry(assessed, line.low.amount, line.high.amount, line.gap.midpoint.amount);
  const trackY = 30;
  const x = (f: number) => f * w;
  const clamp = (v: number, edge: number) => Math.min(Math.max(edge, v), w - edge);
  const bandLo = x(g.low);
  const bandW = Math.max(6, x(g.high) - bandLo);
  const lo = Math.min(x(g.target), x(g.assessed));
  const hi = Math.max(x(g.target), x(g.assessed));
  const bracketY = trackY + 16;
  const caption = say4(line.fractional ? "verdict.assessedMarket" : "verdict.assessed");
  const gapLabel = say4With("line.gap", { amount: money(line.gap.amount), percent: line.gap.percent });
  const label = say4With("line.a11y", {
    caption, assessed: money(line.assessed), low: money(line.low), high: money(line.high),
    amount: money(line.gap.amount), percent: line.gap.percent,
  });
  return (
    <div ref={ref} className="number-line" data-number-line="drawn">
      <svg width={w} height={78} viewBox={`0 0 ${w} 78`} role="img" aria-label={label}>
        <rect x={0} y={trackY - 3} width={w} height={6} rx={3} className="nl-track" />
        <rect x={bandLo} y={trackY - 3} width={bandW} height={6} rx={3} className="nl-band" />
        <circle cx={x(g.assessed)} cy={trackY} r={5.5} className="nl-marker assessed" />
        <circle cx={x(g.target)} cy={trackY} r={5.5} className="nl-marker target" />
        <g className="nl-label" textAnchor="end">
          <text x={clamp(x(g.assessed), 60) + 60} y={8} className="nl-caption">{caption.toUpperCase()}</text>
          <text x={clamp(x(g.assessed), 60) + 60} y={21} className="nl-value assessed">{money(line.assessed)}</text>
        </g>
        <g className="nl-label" textAnchor="start">
          <text x={clamp(x(g.target), 60) - 60} y={8} className="nl-caption" data-held-copy="line.compsImply">
            {say4("line.compsImply").toUpperCase()}
          </text>
          <text x={clamp(x(g.target), 60) - 60} y={21} className="nl-value target">{money(line.gap.midpoint)}</text>
        </g>
        <path d={`M${lo} ${bracketY - 4} L${lo} ${bracketY} L${hi} ${bracketY} L${hi} ${bracketY - 4}`} className="nl-bracket" />
        <text x={clamp((lo + hi) / 2, 80)} y={bracketY + 18} textAnchor="middle" className="nl-gap" data-held-copy="line.gap">
          {gapLabel}
        </text>
      </svg>
    </div>
  );
}

// ── a sheet over the page ──────────────────────────────────────────────────────────────────────────────────────────

/** The app's `.sheet` (a modal form over the page): a bar with "Cancel" and the title, the body, and the sheet's own
 *  bottom action. A modal `dialog`: focus moves into it and returns when it closes, and Escape cancels. */
export function Sheet4({
  titleId,
  cancelId,
  onCancel,
  kind,
  children,
  footer,
}: {
  titleId: Parity4WordingId;
  cancelId: Parity4WordingId;
  onCancel: () => void;
  kind: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const headingId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.current?.focus();
    return () => {
      before?.focus?.();
    };
  }, []);
  return (
    <div className="alert-backdrop">
      <div
        ref={dialog}
        tabIndex={-1}
        className="app-sheet sheet4"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        data-sheet={kind}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            onCancel();
          }
        }}
      >
        <div className="app-sheet-bar">
          <button type="button" className="btn quiet" data-held-copy={cancelId} onClick={onCancel}>{say4(cancelId)}</button>
          <h2 id={headingId} data-held-copy={titleId}>{say4(titleId)}</h2>
          <span />
        </div>
        <div className="app-sheet-body">{children}</div>
        {footer !== undefined && <div className="sheet4-footer">{footer}</div>}
      </div>
    </div>
  );
}
